import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

// The storefront sells fixed packs (10, 20, and 50). Most agents are capped
// at 50 total units per variant; the verified unlimited agent follows only
// Shopify's variant quantity rule, when one exists.
const DEFAULT_MAX_QUANTITY = 50;

/**
 * Fixed quantity selector shared by product, cart, and quick-order surfaces.
 * The rendered buttons are the single source of allowed values; the hidden
 * input keeps native Shopify form serialization working.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput'];

  #handleAgentAccessChange = () => {
    this.#applyMaximumConstraint();
    this.updateButtonStates();
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('astiva:agent-access-change', this.#handleAgentAccessChange);
    this.#applyMaximumConstraint();
    this.updateButtonStates();

    // Product forms must always start on a usable fixed option. Cart and
    // quick-order components intentionally preserve totals such as 0 or 30.
    if (this.localName === 'quantity-selector-component') {
      const selectedOption = this.quantityOptions.find(
        (option) => Number(option.dataset.quantityValue) === Number(this.refs.quantityInput.value)
      );
      if (!selectedOption || selectedOption.disabled) {
        const firstValidOption = this.quantityOptions.find((option) => !option.disabled);
        if (firstValidOption?.dataset.quantityValue) this.setValue(firstValidOption.dataset.quantityValue);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('astiva:agent-access-change', this.#handleAgentAccessChange);
  }

  /** @returns {HTMLButtonElement[]} */
  get quantityOptions() {
    return /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-quantity-value]')));
  }

  /** @returns {number[]} */
  get allowedValues() {
    return this.quantityOptions.map((option) => Number(option.dataset.quantityValue)).filter(Number.isFinite);
  }

  /** @param {Event} event */
  selectQuantity(event) {
    const option = event.target instanceof Element ? event.target.closest('[data-quantity-value]') : null;
    if (!(option instanceof HTMLButtonElement) || option.disabled) return;

    event.preventDefault();
    this.setValue(option.dataset.quantityValue || '');
    this.onQuantityChange();
  }

  /** @param {number} cartQty */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateButtonStates();
  }

  /**
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}}
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const selectedOption = this.quantityOptions.find((option) => Number(option.dataset.quantityValue) === value);
    const wouldExceedMax = max !== null && cartQuantity + value > max;

    return {
      canAdd: Boolean(selectedOption && !selectedOption.disabled && !wouldExceedMax),
      maxQuantity: max,
      cartQuantity,
      quantityToAdd: value,
    };
  }

  /** @returns {string} */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /** @param {string} value */
  setValue(value) {
    this.refs.quantityInput.value = value;
    this.updateButtonStates();
  }

  /** @returns {boolean} */
  isUnlimitedQuantityAgent() {
    return document.body?.dataset.agentUnlimitedQuantity === 'true';
  }

  /** @returns {number|null} */
  getMaximumQuantity() {
    const variantMax = parseIntOrDefault(this.dataset.variantMaxQuantity, null);
    if (this.isUnlimitedQuantityAgent()) return variantMax;
    return variantMax === null ? DEFAULT_MAX_QUANTITY : Math.min(DEFAULT_MAX_QUANTITY, variantMax);
  }

  #applyMaximumConstraint() {
    const maximum = this.getMaximumQuantity();
    if (maximum === null) {
      this.refs.quantityInput.removeAttribute('max');
      delete this.dataset.maxQuantity;
      return;
    }

    this.refs.quantityInput.max = maximum.toString();
    this.dataset.maxQuantity = maximum.toString();
  }

  #updateMaximumRuleText() {
    const maximumRule = this.closest('product-form-component')?.querySelector('[data-quantity-maximum-rule]');
    if (!(maximumRule instanceof HTMLElement)) return;

    const text = this.isUnlimitedQuantityAgent()
      ? maximumRule.dataset.unlimitedText
      : maximumRule.dataset.limitedText;
    if (text) maximumRule.textContent = text;
  }

  /**
   * Preserve a selected fixed value across variant changes when it remains
   * valid; otherwise select the first valid fixed option.
   * @param {string} min
   * @param {string|null} max
   * @param {string} step
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    quantityInput.min = min;
    quantityInput.dataset.min = min;
    quantityInput.step = step;

    const variantMax = parseIntOrDefault(max, null);
    if (variantMax === null) delete this.dataset.variantMaxQuantity;
    else this.dataset.variantMaxQuantity = variantMax.toString();
    this.#applyMaximumConstraint();

    this.updateButtonStates();

    const currentOption = this.quantityOptions.find(
      (option) => Number(option.dataset.quantityValue) === Number(quantityInput.value) && !option.disabled
    );
    if (currentOption) return;

    const firstValidOption = this.quantityOptions.find((option) => !option.disabled);
    if (firstValidOption?.dataset.quantityValue) {
      quantityInput.value = firstValidOption.dataset.quantityValue;
      this.updateButtonStates();
    }
  }

  /**
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.dataset.min || quantityInput.min, 1),
      max: this.getMaximumQuantity(),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.dataset.cartQuantity, 0),
    };
  }

  /** @returns {number|null} */
  getEffectiveMax() {
    const { max, cartQuantity } = this.getCurrentValues();
    return max === null ? null : Math.max(max - cartQuantity, 0);
  }

  /** @param {number} quantity */
  isValidQuantity(quantity) {
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    return quantity >= min && (effectiveMax === null || quantity <= effectiveMax) && (quantity - min) % step === 0;
  }

  updateButtonStates() {
    const value = Number(this.refs.quantityInput.value);
    const serverDisabled = this.dataset.serverDisabled === 'true';

    for (const option of this.quantityOptions) {
      const optionValue = Number(option.dataset.quantityValue);
      option.disabled = serverDisabled || !this.isValidQuantity(optionValue);
      option.setAttribute('aria-pressed', String(optionValue === value));
    }

    this.updateCurrentQuantityLabel();
    this.#updateMaximumRuleText();
  }

  updateCurrentQuantityLabel() {
    const currentQuantity = this.parentElement?.querySelector('[data-current-quantity]');
    if (!currentQuantity) return;

    const value = Number(this.refs.quantityInput.value);
    const isFixedValue = this.allowedValues.includes(value);
    currentQuantity.classList.toggle('hidden', value === 0 || isFixedValue);
    currentQuantity.textContent = `${this.dataset.currentLabel || 'Quantity'}: ${value}`;
  }

  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = Number(quantityInput.value);
    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  updateCartQuantity() {
    this.updateButtonStates();
  }

  get quantityInput() {
    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
